<script setup lang="ts">
import { mdiContentCopy, mdiDownload, mdiCheck, mdiOpenInNew } from '@mdi/js'
import { PYXEL_USER_EXAMPLES_URL } from '~/types/constants'

// 状態はグローバル store 管理（cradle → 魔法研究会 への遷移でも消えないよう、
// centering43 レイアウトに 1 つだけマウントする）。
const store = usePyxelSubmissionStore()
const work = computed(() => store.work)
const dialog = computed({
  get: () => store.visible,
  set: (v: boolean) => {
    if (!v) store.close()
  },
})

const runtimeConfig = useRuntimeConfig()
const { getPublicCreator } = useApi()

// --- 投稿パッケージ（フォーム各フィールドの値）-----------------------------
// 仕様: doc/domain/pyxel_user_examples_submission.md
// ラベルは投稿フォーム（GitHub Discussions user-examples）に合わせた日英併記。
const creationUrl = computed(() => {
  const path = work.value?.publicUrl ?? '' // 例: /works/<slug>
  if (!path) return ''
  return path.startsWith('http') ? path : `${runtimeConfig.public.serviceDomain}${path}`
})

const gifUrl = computed(() => work.value?.coverImageUrl ?? '')

// readonly テキストフィールドで提示する項目
const textFields = computed(() => {
  const w = work.value
  if (!w) return []
  return [
    { key: 'title', label: 'Your Work Title / 作品名', value: w.title, multiline: false },
    { key: 'description', label: 'Description / 作品の説明', value: w.description, multiline: true },
    { key: 'url', label: 'Work URL / 作品URL', value: creationUrl.value, multiline: false },
    { key: 'author', label: 'Your name / あなたの名前', value: w.author, multiline: false },
  ]
})

// --- Contact（名のりに登録された SNS から選択）------------------------------
// X / GitHub アカウントは creator profile (externalLinks) に full URL で保存される。
// 未登録の項目は radio を disabled にし、「なし」のみ選べるようにする。
const xUrl = ref('')
const githubUrl = ref('')
const contactChoice = ref<'x' | 'github' | 'none'>('none')

watch(() => work.value?.authorSlug, async (slug) => {
  xUrl.value = ''
  githubUrl.value = ''
  contactChoice.value = 'none'
  if (!slug) return
  try {
    const creator = await getPublicCreator(slug)
    xUrl.value = creator.externalLinks?.x ?? ''
    githubUrl.value = creator.externalLinks?.github ?? ''
    // 登録済みがあればそれを既定選択（X 優先）。無ければ「なし」。
    contactChoice.value = xUrl.value ? 'x' : githubUrl.value ? 'github' : 'none'
  }
  catch { /* 取得失敗時は「なし」のまま継続 */ }
}, { immediate: true })

const contactValue = computed(() =>
  contactChoice.value === 'x'
    ? xUrl.value
    : contactChoice.value === 'github'
      ? githubUrl.value
      : '',
)

// --- コピー -----------------------------------------------------------------
const copiedKey = ref<string | null>(null)
const snackbar = ref(false)
const snackbarText = ref('')

const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  }
  catch {
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      return true
    }
    catch {
      return false
    }
  }
}

const onCopyField = async (key: string, value: string) => {
  if (!value) return
  const ok = await copyText(value)
  if (ok) {
    copiedKey.value = key
    setTimeout(() => {
      if (copiedKey.value === key) copiedKey.value = null
    }, 2000)
    snackbarText.value = 'コピーしました'
    snackbar.value = true
  }
}

// --- GIF ダウンロード -------------------------------------------------------
// cover GIF は CloudFront / 署名付き S3 URL。`download` 属性は cross-origin で
// 無視されるため、Blob として取得してからダウンロードする。CORS で取得できない
// 場合は新規タブで開いて手動保存にフォールバックする。
const downloading = ref(false)
const onDownloadGif = async () => {
  if (!gifUrl.value) return
  downloading.value = true
  try {
    const res = await fetch(gifUrl.value, { mode: 'cors' })
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = `${work.value?.slug ?? 'pyxel-work'}.gif`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(objectUrl)
  }
  catch (e) {
    console.error('gif download failed, falling back to open', e)
    window.open(gifUrl.value, '_blank', 'noopener')
    snackbarText.value = '別タブで GIF を開きました。右クリックで保存してください'
    snackbar.value = true
  }
  finally {
    downloading.value = false
  }
}

// --- 投稿ページを開く -------------------------------------------------------
const onOpenSubmission = () => {
  window.open(PYXEL_USER_EXAMPLES_URL, '_blank', 'noopener')
}
</script>

<template>
  <Dialog
    v-model="dialog"
    title="Pyxel 公式に投稿してみよう！"
    :submit-button="{ title: 'とじる' }"
    @submit="dialog = false"
  >
    <div class="ue-body">
      <p class="ue-lead">
        クリスタル化おめでとう！🎉<br>
        この作品を Pyxel 公式の
        <a
          href="https://kitao.github.io/pyxel-user-examples/"
          target="_blank"
          rel="noopener"
          class="ue-link"
        >User Examples</a>
        に投稿してみませんか？下の内容をコピーして、投稿フォームに貼り付けてください。
      </p>

      <!-- 投稿エチケット（先に目に入れる） -->
      <div class="ue-etiquette">
        <div class="ue-etiquette-title">
          投稿の前に
        </div>
        <ul>
          <li>自信のある作品を選んで投稿しましょう（連投は控えめに）。</li>
          <li>１作品につき１エントリ。日本語での投稿で大丈夫です。</li>
        </ul>
      </div>

      <!-- 投稿フォームを開く（先に出して、貼り付け先を確保する） -->
      <div class="ue-open">
        <v-btn
          color="primary"
          size="large"
          variant="flat"
          :prepend-icon="mdiOpenInNew"
          @click="onOpenSubmission"
        >
          投稿フォームを開く
        </v-btn>
      </div>

      <!-- 投稿パッケージ（readonly テキストフィールド） -->
      <template
        v-for="f in textFields"
        :key="f.key"
      >
        <v-textarea
          v-if="f.multiline"
          :model-value="f.value"
          :label="f.label"
          readonly
          variant="outlined"
          density="compact"
          hide-details
          rows="2"
          auto-grow
          class="ue-text-field"
        >
          <template #append-inner>
            <v-btn
              :icon="copiedKey === f.key ? mdiCheck : mdiContentCopy"
              size="x-small"
              variant="text"
              :disabled="!f.value"
              :title="copiedKey === f.key ? 'コピーしました' : 'コピー'"
              @click="onCopyField(f.key, f.value)"
            />
          </template>
        </v-textarea>
        <v-text-field
          v-else
          :model-value="f.value"
          :label="f.label"
          readonly
          variant="outlined"
          density="compact"
          hide-details
          class="ue-text-field"
        >
          <template #append-inner>
            <v-btn
              :icon="copiedKey === f.key ? mdiCheck : mdiContentCopy"
              size="x-small"
              variant="text"
              :disabled="!f.value"
              :title="copiedKey === f.key ? 'コピーしました' : 'コピー'"
              @click="onCopyField(f.key, f.value)"
            />
          </template>
        </v-text-field>
      </template>

      <!-- Contact / 連絡先（名のりに登録した SNS から選択） -->
      <fieldset class="ue-fieldset">
        <legend class="ue-fieldset-legend">
          Contact / 連絡先
        </legend>
        <v-radio-group
          v-model="contactChoice"
          hide-details
          density="compact"
          class="ue-radio"
        >
          <v-radio
            value="x"
            :disabled="!xUrl"
          >
            <template #label>
              <span class="ue-radio-label">X{{ xUrl ? `（${xUrl}）` : '（未登録）' }}</span>
            </template>
          </v-radio>
          <v-radio
            value="github"
            :disabled="!githubUrl"
          >
            <template #label>
              <span class="ue-radio-label">GitHub{{ githubUrl ? `（${githubUrl}）` : '（未登録）' }}</span>
            </template>
          </v-radio>
          <v-radio
            value="none"
            label="なし"
          />
        </v-radio-group>
        <div
          v-if="contactValue"
          class="ue-contact-copy"
        >
          <v-btn
            size="x-small"
            variant="text"
            :prepend-icon="copiedKey === 'contact' ? mdiCheck : mdiContentCopy"
            @click="onCopyField('contact', contactValue)"
          >
            {{ copiedKey === 'contact' ? 'コピー済' : '連絡先をコピー' }}
          </v-btn>
        </div>
      </fieldset>

      <!-- Screen Capture GIF / 画面キャプチャGIF -->
      <fieldset class="ue-fieldset">
        <legend class="ue-fieldset-legend">
          Screen Capture GIF / 画面キャプチャGIF（必須）
        </legend>
        <div class="ue-gif-row">
          <img
            v-if="gifUrl"
            :src="gifUrl"
            alt="作品の GIF"
            class="ue-gif-img"
          >
          <div class="ue-gif-actions">
            <v-btn
              size="small"
              variant="tonal"
              :prepend-icon="mdiDownload"
              :loading="downloading"
              :disabled="!gifUrl"
              @click="onDownloadGif"
            >
              GIF をダウンロード
            </v-btn>
            <p class="ue-gif-note">
              GIF をダウンロードして、投稿フォームの「Screen Capture GIF」欄に
              <strong>ドラッグ&ドロップ</strong>してください。
            </p>
          </div>
        </div>
      </fieldset>
    </div>

    <v-snackbar
      v-model="snackbar"
      :timeout="2200"
      location="bottom"
    >
      {{ snackbarText }}
    </v-snackbar>
  </Dialog>
</template>

<style scoped>
/*
  Type scale: ラベル類 (--ue-label) と値類 (--ue-value) を 0.3u 段差で対比。
  Spacing scale: 4 段 (xs/sm/md/lg) に絞ってリズムを統一。
*/
.ue-body {
  /* --- type scale --- */
  --ue-lead: max(12px, calc(1.5 * var(--u)));   /* リード段落 */
  --ue-label: max(10px, calc(1.2 * var(--u)));  /* ラベル類・補助テキスト */
  --ue-value: max(12px, calc(1.5 * var(--u)));  /* 入力値・ラジオ・セクション見出し */

  /* --- spacing scale --- */
  --ue-xs: max(3px, calc(0.4 * var(--u)));
  --ue-sm: max(6px, calc(0.8 * var(--u)));
  --ue-md: max(10px, calc(1.3 * var(--u)));
  --ue-lg: max(14px, calc(1.8 * var(--u)));
  --ue-xl: max(18px, calc(2.4 * var(--u)));

  display: flex;
  flex-direction: column;
  gap: var(--ue-md);
  /* 親 .fantasy-dialog-text は固定高さ。コンテンツが長いとダイアログ枠から
     はみ出すため、ここで高さを満たしつつ内部スクロールに収める。 */
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  /* スクロールバー出現時も左右対称に保つ */
  scrollbar-gutter: stable both-edges;
  padding-inline: var(--ue-xs);
}

.ue-lead {
  font-size: var(--ue-lead);
  line-height: 1.6;
  margin: 0;
}

.ue-link {
  color: #1565c0;
  text-decoration: underline;
}

/* 入力テキスト */
.ue-text-field :deep(.v-field__input) {
  font-size: var(--ue-value);
}

/* Vuetify フローティングラベル（ラベル類は全て --ue-label に統一） */
.ue-text-field :deep(.v-label) {
  font-size: var(--ue-label);
}

/* Vuetify outlined テキストフィールド風の枠（Contact / GIF を統一） */
.ue-fieldset {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: var(--ue-md) var(--ue-md) var(--ue-md);
  margin: 0;
  min-inline-size: 0; /* fieldset 既定の min-width: min-content を打ち消す */
}

.ue-fieldset-legend {
  font-size: var(--ue-label);
  font-weight: 400;
  color: rgba(0, 0, 0, 0.5);
  padding: 0 max(3px, calc(0.4 * var(--u)));
  /* 現状の --u（≒ 8px 前後）で -12px 相当。--u に追従して画面サイズで可変。 */
  margin-inline-start: calc(-1.5 * var(--u));
  user-select: none;
}

.ue-radio :deep(.v-selection-control) {
  min-height: max(22px, calc(2.6 * var(--u)));
}

.ue-radio-label {
  font-size: var(--ue-value);
  word-break: break-all;
}

.ue-contact-copy {
  text-align: right;
}

.ue-gif-row {
  display: flex;
  gap: var(--ue-md);
  align-items: flex-start;
}

.ue-gif-img {
  width: max(72px, calc(9 * var(--u)));
  height: auto;
  image-rendering: pixelated;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex: 0 0 auto;
}

.ue-gif-actions {
  display: flex;
  flex-direction: column;
  gap: var(--ue-sm);
}

.ue-gif-note {
  font-size: var(--ue-label);
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.5;
}

.ue-etiquette {
  padding: var(--ue-md) var(--ue-md) var(--ue-md) var(--ue-md);
  background: rgba(0, 100, 180, 0.05);
  border-left: 3px solid rgba(0, 100, 180, 0.4);
  border-radius: 4px;
}

.ue-etiquette-title {
  font-size: var(--ue-value);
  font-weight: bold;
  margin-bottom: var(--ue-sm);
}

.ue-etiquette ul {
  margin: 0;
  padding-left: max(14px, calc(1.8 * var(--u)));
}

.ue-etiquette li {
  font-size: var(--ue-label);
  line-height: 1.6;
}

.ue-etiquette li + li {
  margin-top: var(--ue-xs);
}

.ue-open {
  display: flex;
  justify-content: center;
}
</style>
